import React, { useState, useEffect } from "react";
import { Filter , RefreshCw } from "lucide-react";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import TablePagination from "./TablePagination";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import CircularProgress from '@mui/material/CircularProgress';
import ViewColModal from './ViewColModal'

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function Table({ data ,filter, setFilter,setRefresh ,loading}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentLogs, setCurrentLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [formFilter, setFormFilter] = useState(filter);
  const [searchCol, setSearchCol] = useState([]);
  const [filteredData, setFilteredData] = useState([]);  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] =useState('');
  const [title, setTitle] = useState('');


  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleCloseFilter = () => {
    setOpen(false);
  };
  const handleSaveFilter = () => {
    setFilter(formFilter);
    handleRefresh();
    setOpen(false);
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? currentLogs.map((log) => log.ts) : []);
  };

  const handleSelectRow = (ts) => {
    setSelectedRows((prev) =>
      prev.includes(ts) ? prev.filter((key) => key !== ts) : [...prev, ts]
    );
  };
  const handleRefresh = () => {
    setRefresh(prev => !prev)
  };
  useEffect(() => {
    if (searchCol && searchCol.type === 'search') {
      const { column, value } = searchCol;
      if (value === '') {
        setFilteredData(data);
      } else {
        const lowerValue = value.toLowerCase().trim();
        const filtered = data.filter((item) =>{
          return item[column]?.toString().toLowerCase().includes(lowerValue)
        }
        );
        setFilteredData(filtered);
      }
    }
  }, [searchCol]);

  useEffect(() => {
    setFilteredData(data)
  }, [data]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setCurrentLogs(filteredData.slice(startIndex, endIndex));
  }, [currentPage, rowsPerPage, filteredData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);
  return (
    <>
      <div className="table-box">
        <div className="logs-table">
          <div className="table-header">
            <button   aria-label="filter-btn" className="filter-button" onClick={handleClickOpen}>
              <Filter size={16} />
              Add Filter
            </button>
            <button  aria-label="run" className="filter-button" onClick={handleRefresh}>
              Refresh 
             <RefreshCw size={16} />
            </button>
            {/* <div className="action-buttons">
              <button  aria-label="export" className="export-button">Export</button>
              <button  aria-label="run" className="run-button">Run</button>
            </div> */}
          </div>
          <div style={{ overflowX: 'auto' }}>
          {loading ? <div className='loading'><CircularProgress color="inherit" /></div> :
          <table>
            <TableHeader
              selectedAll={selectedRows.length === currentLogs.length}
              onSelectAll={handleSelectAll}
              setSearchCol={setSearchCol}
            />
            <tbody>
               {currentLogs.map((row) => (
                <TableRow
                  key={row.ts}
                  row={row}
                  selected={selectedRows.includes(row.ts)}
                  onSelect={handleSelectRow}
                  setModalOpen={setModalOpen}
                  setSelectedContent={setSelectedContent}
                  setTitle={setTitle}
                />
              ))}
            </tbody>
          </table>
          }
          </div>

          <TablePagination
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalRows={filteredData.length}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </div>
      </div>
      <BootstrapDialog
        onClose={handleCloseFilter}
        aria-labelledby="customized-dialog-title"
        disableScrollLock={true}
        open={open}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, background: "var(--bg-body)" }}
          id="customized-dialog-title"
        >
          Add Filter
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseFilter}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "grid", gridTemplateTolumns:"auto auto",gap:'20px 10px' }}
          >
            <TextField
              label="Dashboard ID"
              variant="outlined"
              fullWidth
              value={formFilter.dashboardId}
              onChange={(e) =>
                setFormFilter({ ...formFilter, dashboardId: e.target.value })
              }
            />

            <TextField
              label="Panel ID"
              variant="outlined"
              fullWidth
              value={formFilter.panelId}
              onChange={(e) =>
                setFormFilter({ ...formFilter, panelId: e.target.value })
              }
            />

            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formFilter.startDate}
              onChange={(e) =>
                setFormFilter({ ...formFilter, startDate: e.target.value })
              }
            />

            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formFilter.endDate}
              onChange={(e) =>
                setFormFilter({ ...formFilter, endDate: e.target.value })
              }
            />

            <TextField
              sx={{
                gridColumn: '1 / span 2'
              }}
              label="Interval"  
              variant="outlined"
              fullWidth
              value={formFilter.interval}
              onChange={(e) =>
                setFormFilter({ ...formFilter, interval: e.target.value })
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button   aria-label="save button" autoFocus onClick={handleSaveFilter}>
            Save changes
          </Button>
        </DialogActions>
      </BootstrapDialog>
      <ViewColModal 
        title={title} 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        content={selectedContent} 
      />
    </>
  );
}

export default Table;
